import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { useAuthStore } from '../../store/useAuthStore';
import {
  createAppointment,
  deleteAppointment,
  getAppointment,
  listDossierAppointments,
  updateAppointment,
} from '../../services/dossiers/appointmentService';

function invalidateAppointments(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['appointments'] });
  qc.invalidateQueries({ queryKey: ['calendar', 'sources'] });
}

export function useAppointment(id: string | null) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: ['appointments', id ?? ''],
    queryFn: () => getAppointment(id!),
    enabled: !!id,
  });
}

export function useDossierAppointments(dossierId: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: ['appointments', 'dossier', dossierId ?? ''],
    queryFn: () => listDossierAppointments(dossierId!),
    enabled: !!dossierId,
  });
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.collaborator);
  return useMutation({
    mutationFn: (input: {
      title: string;
      description?: string | null;
      starts_at: string;
      ends_at: string;
      dossier_id: string | null;
      event_type_id?: string | null;
      participantIds: string[];
    }) => createAppointment({ ...input, created_by: me?.id ?? null }),
    onSuccess: () => invalidateAppointments(qc),
  });
}

export function useUpdateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: {
        title: string;
        description?: string | null;
        starts_at: string;
        ends_at: string;
        dossier_id: string | null;
        event_type_id?: string | null;
        participantIds: string[];
      };
    }) => updateAppointment(id, patch),
    onSuccess: () => invalidateAppointments(qc),
  });
}

export function useDeleteAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAppointment(id),
    onSuccess: () => invalidateAppointments(qc),
  });
}
